import { cn } from '@workspace/ui/lib/utils'
import { Check, Minus } from 'lucide-react'

export interface StepperStep {
  id: string
  label: string
  done: boolean
  /**
   * Blocks interaction. Use for steps that are not reachable yet, so a stepper
   * can show the whole journey without implying every part of it is clickable.
   */
  disabled?: boolean
  /**
   * A step that was skipped rather than completed — the journey took a path
   * that never required it. Renders muted with a dash instead of a number, so
   * it neither claims completion (a checkmark would) nor reads as still
   * upcoming (a plain pending circle would). Ignored once `done` is true: a
   * step that did complete never renders as merely optional.
   */
  optional?: boolean
}

export interface StepperProps {
  steps: StepperStep[]
  /** The step the user is on. Gets the ring treatment. */
  currentId?: string | null
  /**
   * Omit to render a read-only stepper — a progress indicator rather than a
   * navigation control.
   */
  onStepClick?: (id: string) => void
  /**
   * Size each step to its label instead of splitting the width evenly.
   *
   * Even distribution suits a full-width checklist; a compact progress
   * indicator with a long label ("Profile & Proof") otherwise gets squeezed
   * into a column of wrapped words.
   */
  compact?: boolean
  className?: string
}

/**
 * Horizontal progress stepper: numbered circles, connectors, done/current/pending.
 *
 * Lifted out of the outreach launch panel, where it was already prop-driven and
 * state-free but unexported — so the Home wizard's progress rail would have been
 * a second implementation of the same thing.
 *
 * Completed steps render as a record rather than an action: going "back" to a
 * finished step is not a thing the user needs, and making it clickable invites
 * the question of what that would even do.
 */
export function Stepper({ steps, currentId, onStepClick, compact, className }: StepperProps) {
  return (
    <ol className={cn('flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-0', className)}>
      {steps.map((step, index) => {
        const isCurrent = step.id === currentId
        const isActionable = !step.done && !step.disabled && !!onStepClick
        const isOptional = step.optional && !step.done

        const content = (
          <>
            <span
              className={cn(
                'flex size-7 shrink-0 items-center justify-center rounded-full text-[12px] font-bold transition-shadow',
                step.done
                  ? 'bg-emerald-500/15 text-emerald-400'
                  : isOptional
                    ? 'border border-dashed border-border/70 bg-card text-muted-foreground/70'
                    : isCurrent
                      ? 'border border-primary text-primary shadow-[0_0_0_4px_rgba(249,115,22,0.12)]'
                      : 'border border-dashed border-border bg-card text-muted-foreground'
              )}
            >
              {step.done ? (
                <Check className="size-3.5" strokeWidth={3} />
              ) : isOptional ? (
                <Minus className="size-3" strokeWidth={3} />
              ) : (
                index + 1
              )}
            </span>
            <span
              className={cn(
                'whitespace-nowrap text-[12.5px] font-medium',
                step.done
                  ? 'text-foreground/85'
                  : isOptional
                    ? 'text-muted-foreground/70'
                    : isCurrent
                      ? 'text-foreground'
                      : cn(
                          'text-muted-foreground',
                          isActionable && 'transition-colors group-hover:text-foreground'
                        )
              )}
            >
              {step.label}
            </span>
          </>
        )

        return (
          <li
            key={step.id}
            className={cn('flex items-center gap-3', !compact && 'sm:flex-1')}
            aria-current={isCurrent ? 'step' : undefined}
          >
            {isActionable ? (
              <button
                type="button"
                onClick={() => onStepClick(step.id)}
                className="group flex items-center gap-2.5 text-left"
              >
                {content}
              </button>
            ) : (
              <div className="flex cursor-default items-center gap-2.5">{content}</div>
            )}
            {index < steps.length - 1 && (
              <span
                aria-hidden
                className={cn(
                  // min-width keeps the connector visible when a label wraps and
                  // would otherwise squeeze the flex-1 line down to nothing.
                  'hidden h-px sm:mx-3 sm:block sm:min-w-6',
                  compact ? 'sm:w-8' : 'flex-1',
                  step.done ? 'bg-emerald-500/40' : 'bg-border/60'
                )}
              />
            )}
          </li>
        )
      })}
    </ol>
  )
}
